import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildTextFieldSx, fieldLabelSx } from '../common/formFieldStyles';
import useStoreCustomerDropdown from '../../hooks/useStoreCustomerDropdown';
import {
  filterCustomerDropdownOptions,
  toCustomerDropdownItem,
} from '../../utils/customerDropdownUtils';

const CustomerDropdownOption = memo(({ customer }) => {
  const contactLine = customer?.contactLine;

  return (
    <Box sx={{ py: 0.25, minWidth: 0, width: '100%' }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.3 }}>
        {customer?.name}
      </Typography>
      {contactLine ? (
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.7rem',
            color: 'text.secondary',
            lineHeight: 1.35,
            mt: 0.15,
          }}
        >
          {contactLine}
        </Typography>
      ) : null}
    </Box>
  );
});

CustomerDropdownOption.displayName = 'CustomerDropdownOption';

const CustomerDropdown = ({
  storeId,
  value = null,
  onChange,
  label,
  placeholder,
  error = false,
  helperText,
  disabled = false,
  required = false,
  enabled = true,
}) => {
  const { t } = useTranslation();
  const { customers, loading } = useStoreCustomerDropdown(storeId, enabled);
  const selectedId = value?.customerId || value?.id || null;
  const [inputValue, setInputValue] = useState(value?.name || '');

  const selectedFromOptions = useMemo(() => {
    if (!selectedId) {
      return null;
    }

    return customers.find((customer) => String(customer.customerId) === String(selectedId))
      || toCustomerDropdownItem(value);
  }, [customers, selectedId, value]);

  const options = useMemo(() => {
    if (!selectedFromOptions) {
      return customers;
    }

    if (customers.some((customer) => String(customer.customerId) === String(selectedFromOptions.customerId))) {
      return customers;
    }

    return [selectedFromOptions, ...customers];
  }, [customers, selectedFromOptions]);

  useEffect(() => {
    if (selectedFromOptions?.name) {
      setInputValue(selectedFromOptions.name);
    }
  }, [selectedFromOptions]);

  const filterOptions = useCallback((currentOptions) => (
    filterCustomerDropdownOptions(currentOptions, inputValue)
  ), [inputValue]);

  const handleChange = useCallback((_, nextValue) => {
    onChange?.(nextValue);
    setInputValue(nextValue?.name || '');
  }, [onChange]);

  const handleInputChange = useCallback((_, nextValue, reason) => {
    if (reason === 'reset') {
      return;
    }

    setInputValue(nextValue);
  }, []);

  const getOptionKey = useCallback((option) => String(option?.customerId || ''), []);
  const getOptionLabel = useCallback((option) => option?.name || '', []);
  const isOptionEqualToValue = useCallback((option, optionValue) => (
    String(option?.customerId) === String(optionValue?.customerId)
  ), []);

  const renderOption = useCallback((props, option) => {
    const { key, ...rest } = props;
    return (
      <li {...rest} key={String(option.customerId || key)}>
        <CustomerDropdownOption customer={option} />
      </li>
    );
  }, []);

  return (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      {label ? (
        <Typography variant="subtitle2" sx={{ ...fieldLabelSx, fontWeight: 600 }}>
          {label}
          {required ? ' *' : ''}
        </Typography>
      ) : null}
      <Autocomplete
        options={options}
        value={selectedFromOptions}
        inputValue={inputValue}
        loading={loading}
        disabled={disabled}
        autoHighlight
        openOnFocus
        clearOnBlur={false}
        filterOptions={filterOptions}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        noOptionsText={t('customersManagement.dropdown.noOptions')}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || t('customersManagement.dropdown.searchPlaceholder')}
            error={error}
            helperText={helperText}
            autoComplete="off"
            sx={buildTextFieldSx(error)}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
              name: 'customer-lookup',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        slotProps={{
          listbox: {
            sx: {
              maxHeight: 320,
              padding: 0,
            },
          },
        }}
      />
    </Box>
  );
};

export default CustomerDropdown;
export { CustomerDropdownOption };
